import React, { useEffect, useState } from "react";
import { Volume2, VolumeX, BellRing, X } from "lucide-react";
import { notificationAudio } from "../lib/notificationSound";

/**
 * Slide-down drawer attached to the admin CommandHeader. Lets dispatchers:
 *   • Enable/disable alert sounds globally
 *   • Adjust volume 0–100 %
 *   • Trigger a test beep to validate their speakers
 *
 * Persistence is handled by `notificationAudio.setAdminSettings()` →
 * localStorage `rr_admin_audio`. Survives page reloads + tab navigations.
 */
export const AdminAudioSettings = ({ open, onClose }) => {
  const [enabled, setEnabled] = useState(true);
  const [volume, setVolume] = useState(0.8);

  useEffect(() => {
    if (!open) return;
    const s = notificationAudio.getAdminSettings();
    Promise.resolve().then(() => {
      setEnabled(s.enabled);
      setVolume(s.volume);
    });
  }, [open]);

  const persist = (next) => {
    notificationAudio.setAdminSettings(next);
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-end pt-24 pr-4 sm:pr-8" data-testid="admin-audio-settings">
      <button
        type="button"
        className="absolute inset-0 bg-black/60"
        onClick={onClose}
        aria-label="Fermer"
      />
      <div className="relative w-80 max-w-[92vw] bg-[#0f1318] border border-emerald-500/40 shadow-[0_8px_40px_rgba(0,0,0,0.7)] cmd-scanlines">
        <div className="bg-black border-b border-emerald-500/40 px-3 py-2 flex items-center gap-2">
          <BellRing className="w-4 h-4 text-emerald-400" strokeWidth={2.5} />
          <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-300">Notifications sonores</span>
          <button
            type="button"
            onClick={onClose}
            className="ml-auto p-0.5 hover:bg-zinc-800 text-zinc-400 hover:text-zinc-100"
            data-testid="audio-settings-close"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>

        <div className="p-3 space-y-4">
          <label className="flex items-center justify-between gap-2 cursor-pointer">
            <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-zinc-200">Sons activés</span>
            <button
              type="button"
              role="switch"
              aria-checked={enabled}
              data-testid="audio-toggle-enabled"
              onClick={() => {
                const next = !enabled;
                setEnabled(next);
                persist({ enabled: next });
              }}
              className={`relative w-10 h-5 transition-colors ${enabled ? "bg-emerald-500" : "bg-zinc-700"}`}
            >
              <span className={`absolute top-0.5 w-4 h-4 bg-white transition-transform ${enabled ? "translate-x-5" : "translate-x-0.5"}`} />
            </button>
          </label>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-zinc-200">Volume</span>
              <span className="font-mono text-[10px] tabular-nums text-emerald-300" data-testid="audio-volume-value">{Math.round(volume * 100)}%</span>
            </div>
            <input
              type="range"
              min="0"
              max="100"
              step="1"
              value={Math.round(volume * 100)}
              onChange={(e) => {
                const next = parseInt(e.target.value, 10) / 100;
                setVolume(next);
                persist({ volume: next });
              }}
              data-testid="audio-volume-slider"
              disabled={!enabled}
              className="w-full accent-emerald-500 disabled:opacity-50"
            />
            <div className="flex items-center justify-between text-[9px] text-zinc-500 mt-1 font-mono">
              <span>0% Muet</span>
              <span>100% Max</span>
            </div>
          </div>

          <button
            type="button"
            onClick={() => notificationAudio.playAdminAlert()}
            data-testid="audio-test-btn"
            disabled={!enabled || volume <= 0}
            className="w-full border border-emerald-500/50 text-emerald-300 py-2 text-[10px] font-bold uppercase tracking-[0.2em] hover:bg-emerald-500 hover:text-black transition-colors disabled:opacity-40 flex items-center justify-center gap-1.5"
          >
            <Volume2 className="w-3.5 h-3.5" /> Tester le son
          </button>

          <p className="text-[10px] text-zinc-500 leading-snug">
            Les sons jouent automatiquement à la réception d&apos;une nouvelle alerte SOS, même si vous êtes sur un autre onglet. Ils respectent ce réglage. Stockage local de l&apos;appareil ; aucune télémétrie.
          </p>
        </div>
      </div>
    </div>
  );
};
